/**
 * `IAIPromptStoreModel.ts` — the agent-facing Markdown the model is given: role
 * prompts, skill prompts, personalities, the base prompt, and the workflow
 * schema. Reads the deployment's published copy, falling back to the file that
 * shipped in the build.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client reads these
 * from `StreamingAssets/AI/**` on disk, where editing one means editing a file.
 * A browser has no writable source tree, so the AI debug panel offers the same
 * thing as download / upload / reset and this model is where a published file
 * lives.
 *
 * ## Publishing REPLACES; it does not shadow
 *
 * This replaced a debug-gated override layer, and the difference is the whole
 * point of the model. The old read was:
 *
 * ```ts
 * getOverrideText(kind, fileName) {
 *   if (!this.m_debug.isEnabled) return null;   // ← potato off = shipped file
 * ```
 *
 * so an uploaded prompt applied only for developers who had typed `potato`. The
 * same account therefore ran two different agents depending on who was looking,
 * and the debug panel's tabs — which exist to answer "what is the model actually
 * told" — showed the reader's own view rather than the product's. Uploading a
 * prompt now changes what Spark is, for everyone on this deployment.
 *
 * The gate remains on the WRITE side only ({@link publishAsync}), because the
 * only affordance that publishes or resets one is the debug dialog; nothing
 * should be able to publish from a build showing no developer surfaces.
 *
 * ## The bundled file is the SEED
 *
 * A file with no published entry loads from `public/AI/**`, so a fresh account
 * runs the shipped prompts and {@link resetAsync} is a real action — delete the
 * published copy, fall back to the build's — rather than "stop shadowing".
 *
 * ## Scoped per DEPLOYMENT
 *
 * One document per deployment (see `models/data/agentPromptsDto.ts`). A named
 * channel exists to run a different VERSION of the app against the same data, so
 * an account-wide document would shadow a channel's own bundled prompts and make
 * a channel useless for testing a prompt change — which is what channels are most
 * often wanted for. Publishing on one channel therefore does not reach the
 * others, and promoting a prompt means publishing it again on the default
 * deployment.
 *
 * ## Contracts a caller depends on
 *
 * **Reads are synchronous and never fail**, because the catalogs call
 * {@link getPromptText} per file while building a definition. The document has to
 * be in memory by then, which is what {@link ensureLoadedAsync} provides and why
 * `AIInterfaceModel.ensureContentLoadedAsync` awaits it BEFORE any catalog.
 *
 * **A failed read is never "nothing published".** The storage layer throws on
 * anything that is not a genuine 404/403, and a throw leaves the previous cache
 * in place and records {@link lastError}. Treating a transient failure as an
 * empty document would silently drop every published prompt back to the shipped
 * ones — and the next publish would write a document missing all of them.
 */

import type { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IDeploymentConfigModel } from "@models/config/IDeploymentConfigModel";
import type { AgentPromptKind } from "@models/data/agentPromptsDto";

// Re-exported so a caller needs one import site for the model and its key type.
export type { AgentPromptKind };

/**
 * The narrow READ seam every catalog takes. They are pure loaders and must not
 * know about storage, deployments, or the Emitter — only that some text may
 * stand in for the file they were about to fetch.
 */
export interface AgentPromptSource {
  /**
   * The published text for `{kind}/{fileName}`, or null to load the file that
   * shipped in the build.
   */
  getPromptText(kind: AgentPromptKind, fileName: string): string | null;
}

/** One published file, for listing and badging. */
export interface AgentPromptRecord {
  readonly kind: AgentPromptKind;
  readonly fileName: string;
  /** Epoch ms it was published. */
  readonly at: number;
  /** Who published it. Empty when the document predates attribution. */
  readonly by: string;
}

/**
 * Why a publish was refused. `blank` is the one that matters: an empty file
 * cannot produce a definition, so the catalog loader would reject the whole
 * replacement and keep the previous live catalog. Persisting it would make every
 * later load fail until someone reset it, so it is refused at the boundary.
 */
export type AgentPromptWriteError = "blank" | "gated" | "storage";

export type AgentPromptWriteResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: AgentPromptWriteError };

export interface IAIPromptStoreModel extends AgentPromptSource {
  /** Fires whenever the published set changes — a publish, a reset, or a reload. */
  readonly onChanged: Emitter<void>;

  /** True once a load has settled, successfully or not. */
  readonly isLoaded: boolean;

  /** Message from the last failed load or publish, else null. */
  readonly lastError: string | null;

  /**
   * Whether ANY file is published on this deployment. The guard on the
   * reload-on-change subscription: a deployment running stock prompts must not
   * re-fetch every catalog on an unrelated event.
   */
  readonly hasPublishedPrompts: boolean;

  /** Whether this specific file is running a published copy rather than the build's. */
  isPublished(kind: AgentPromptKind, fileName: string): boolean;

  /** When it was published, or null. */
  getPublishedAt(kind: AgentPromptKind, fileName: string): number | null;

  /**
   * Who published it, or null. Worth surfacing: "this prompt is edited" and "you
   * edited this prompt" are different facts, and only one of them tells a
   * developer whether the agent's behaviour is their own doing.
   */
  getPublishedBy(kind: AgentPromptKind, fileName: string): string | null;

  listPublished(): ReadonlyArray<AgentPromptRecord>;

  /**
   * Keys + publish stamps of the CURRENT set, joined into one string.
   *
   * `onChanged` fires for load settles as well as publishes, and the first load
   * at boot is one of them — so a subscriber that reloads its catalogs on every
   * event would do a redundant full re-fetch immediately after the boot pass
   * whenever anything is published. Comparing this is what limits a reload to a
   * real change, including one made by someone else that arrives as a reload.
   */
  readonly publishedSignature: string;

  /** Loads once. Concurrent callers share the in-flight promise. */
  ensureLoadedAsync(): Promise<void>;

  /**
   * Publishes a file for everyone on this deployment. Resolves once the write
   * has landed, so a caller that reloads the catalogs afterwards is reloading
   * against the published text.
   */
  publishAsync(
    kind: AgentPromptKind,
    fileName: string,
    text: string,
  ): Promise<AgentPromptWriteResult>;

  /** Deletes the published copy so the build's file takes over again. */
  resetAsync(kind: AgentPromptKind, fileName: string): Promise<boolean>;

  /** Resets every published file on this deployment. */
  resetAllAsync(): Promise<boolean>;

  /**
   * Transport, attached once auth exists. See `Campfire_Web_Runtime.md` for the
   * register-early / attach-later pattern.
   *
   * `auth` is used ONLY to stamp `uploadedBy` and to obtain the token a WRITE
   * needs — a read is unauthenticated, so the prompts this deployment runs are
   * known before anyone signs in.
   */
  attachTransport(auth: IAuthModel, config: IDeploymentConfigModel): void;
}

export const AI_PROMPT_STORE_MODEL = Symbol.for("Campfire.AIPromptStoreModel");
